import opentype from "opentype.js";
import {
  TextAlignmentType,
  TextCaseType,
  TextDirectionType,
} from "../../../interfaces";
import { FontLoader } from "../../../helpers";
import { FabricPath, util } from "../../../objects";
import { renderGlyphSegments, GlyphSegment } from "./glyphRenderer";

const DefaultFontSrc = "/fonts/default_font.ttf";

export default class SVGText extends FabricPath {
  fontSize: number = 0;
  maxFontSize: number = 200;
  fontSizeScalable: boolean = false;
  font?: opentype.Font;
  fontPath?: string;
  fontSrc?: string;
  fontFamily: string = "";

  text: string = "";
  prefix: string = "";
  infix: string = "";
  suffix: string = "";
  textAlign: TextAlignmentType = TextAlignmentType.Center;
  textCase: TextCaseType = TextCaseType.None;
  textDirection: TextDirectionType = TextDirectionType.Horizontal;
  charSpacing: number = 0;
  lineHeight: number = 1;
  patternImagePath?: string;
  patternScale: number = 1;
  patternFit: "line" | "cover" = "line";
  textColorsByLetter: boolean = false;
  letterColors: string[] = [];
  private glyphSegments: GlyphSegment[] = [];

  constructor(text: string, options: any = {}) {
    super("");
    this.set(options);
  }

  set(key: string | Record<string, any>, value?: any): this {
    super.set(key, value);

    const keysToTriggerUpdate = [
      "text",
      "font",
      "fontSize",
      "charSpacing",
      "lineHeight",
      "textAlign",
      "fontFamily",
      "textDirection",
      "textColorsByLetter",
      "letterColors",
    ];

    let shouldUpdate = false;

    if (typeof key === "string") {
      if (keysToTriggerUpdate.includes(key)) {
        shouldUpdate = true;
      }
    } else if (typeof key === "object") {
      shouldUpdate = Object.keys(key).some((k) =>
        keysToTriggerUpdate.includes(k),
      );
    }

    if (shouldUpdate) {
      this.updateSVG();
    }

    return this;
  }

  rerender() {
    this.updateSVG();
    this.canvas?.requestRenderAll();
  }

  protected updateSVG() {
    const {
      font,
      text,
      fontSize,
      charSpacing,
      lineHeight,
      textAlign,
      textDirection,
    } = this;
    if (!font) return;

    const textPathResult = createMultilineTextPath(
      font,
      text,
      fontSize,
      charSpacing,
      lineHeight,
      textAlign,
      textDirection,
      {
        textColorsByLetter: this.textColorsByLetter,
        letterColors: this.letterColors,
        fallbackColor: typeof this.fill === "string" ? this.fill : "#000000",
      },
    );
    const path = textPathResult.path.toPathData(10);
    this.glyphSegments = textPathResult.glyphSegments;
    this.set("path", util.parsePath(path));
    this.setBoundingBox();
    this.canvas?.requestRenderAll();
  }

  _render(ctx: CanvasRenderingContext2D) {
    if (!this.isByLetterMode()) {
      super._render(ctx);
      return;
    }
    const paintStrokeFirst = this.paintFirst === "stroke";

    if (paintStrokeFirst) {
      this.renderFabricStroke(ctx);
      this.renderByLetterFill(ctx);
      return;
    }

    this.renderByLetterFill(ctx);
    this.renderFabricStroke(ctx);
  }

  private renderByLetterFill(ctx: CanvasRenderingContext2D) {
    ctx.save();
    ctx.translate(-this.pathOffset.x, -this.pathOffset.y);
    renderGlyphSegments(ctx, this.glyphSegments);
    ctx.restore();
  }

  private renderFabricStroke(ctx: CanvasRenderingContext2D) {
    // Reuse Fabric's stroke pipeline to preserve strokeUniform, dash, shadow logic.
    (this as any)._renderPathCommands(ctx);
    (this as any)._renderStroke(ctx);
  }

  async loadFontFromUrl(src?: string) {
    try {
      this.font = await FontLoader.loadOpenTypeFont(src || DefaultFontSrc);
    } catch (error) {
      console.error("Failed to load font:", error);
    }
    if (!this.font) {
      this.font = await FontLoader.loadOpenTypeFont(DefaultFontSrc);
    }
    this.updateSVG();
    this.rerender();
  }

  private isByLetterMode(): boolean {
    return (
      this.textColorsByLetter &&
      this.glyphSegments.length > 0 &&
      typeof Path2D !== "undefined"
    );
  }
}

function createMultilineTextPath(
  font: opentype.Font,
  text: string,
  fontSize: number,
  charSpacing: number = 0,
  lineHeightRatio: number = 1,
  textAlign: "left" | "center" | "right" = "left",
  textDirection: "horizontal" | "vertical" = "horizontal",
  colorConfig: {
    textColorsByLetter?: boolean;
    letterColors?: string[];
    fallbackColor?: string;
  } = {},
): { path: opentype.Path; glyphSegments: GlyphSegment[] } {
  const path = new opentype.Path();
  const glyphSegments: GlyphSegment[] = [];
  const useByLetter =
    !!colorConfig.textColorsByLetter &&
    Array.isArray(colorConfig.letterColors) &&
    colorConfig.letterColors.length > 0;
  const letterColors = colorConfig.letterColors || [];
  const fallbackColor = colorConfig.fallbackColor || "#000000";
  let colorCharIndex = 0;

  const appendGlyph = (glyphPath: opentype.Path, rawChar?: string) => {
    path.extend(glyphPath);
    if (!useByLetter) return;
    if (!rawChar || /\s/u.test(rawChar)) return;

    const color =
      letterColors[colorCharIndex % letterColors.length] || fallbackColor;
    colorCharIndex += 1;

    const pathData = glyphPath.toPathData(10);
    if (!pathData) return;
    glyphSegments.push({ pathData, color });
  };

  const fontScale = fontSize / font.unitsPerEm;
  const lines = text.split("\n");
  const lineHeight = fontSize * lineHeightRatio;
  const charHeight = lineHeight;
  const halfFontEm = font.unitsPerEm / 2;

  const { ascender: asc, descender: des } =
    FontLoader.getFontAscenderAndDescender(font);
  const ascender = asc * fontScale;
  const descender = des * fontScale;

  if (textDirection === "vertical") {
    const totalColumnsWidth = lines.length * charHeight;
    const baseOffset =
      textAlign === "center"
        ? -totalColumnsWidth / 2 + charHeight / 2
        : textAlign === "right"
          ? -totalColumnsWidth + charHeight
          : 0;

    const deltaY = fontSize + charSpacing;

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      const chars = Array.from(line);
      const glyphs: opentype.Glyph[] = [];
      const glyphWidths: number[] = [];
      let lineWidth = 0;

      for (const ch of chars) {
        const g = font.charToGlyph(ch);
        glyphs.push(g);
        const gw = (g.advanceWidth || halfFontEm) * fontScale;
        glyphWidths.push(gw);
        if (gw > lineWidth) lineWidth = gw;
      }

      const baseX = i * charHeight + baseOffset - lineWidth / 2;
      let y = 0;

      glyphs.forEach((g, glyphIndex) => {
        const gw = glyphWidths[glyphIndex];
        const rawChar = chars[glyphIndex];

        let x = baseX;
        if (textAlign === "center") {
          x += (lineWidth - gw) / 2;
        } else if (textAlign === "right") {
          x += lineWidth - gw;
        }

        appendGlyph(g.getPath(x, y, fontSize), rawChar);
        y += deltaY;
      });
    }

    const maxCharsInColumn = Math.max(...lines.map((l) => l.length), 0);
    const lastBaselineY = Math.max(0, maxCharsInColumn - 1) * deltaY;
    path.moveTo(0, -ascender);
    path.lineTo(0.001, -ascender);
    path.moveTo(0, lastBaselineY - descender);
    path.lineTo(0.001, lastBaselineY - descender);
  } else {
    for (let li = 0; li < lines.length; li++) {
      const line = lines[li];
      const chars = Array.from(line);
      const glyphs = chars.map((ch) => font.charToGlyph(ch));
      const kerns = glyphs.map((g, i) =>
        i < glyphs.length - 1
          ? font.getKerningValue(g, glyphs[i + 1]) * fontScale
          : 0,
      );

      const lineWidth = glyphs.reduce((sum, g, i) => {
        const adv = (g.advanceWidth || 0) * fontScale;
        return sum + adv + kerns[i] + (i < kerns.length - 1 ? charSpacing : 0);
      }, 0);

      let x =
        textAlign === "center"
          ? -lineWidth / 2
          : textAlign === "right"
            ? -lineWidth
            : 0;
      const y = li * lineHeight;

      glyphs.forEach((g, glyphIndex) => {
        appendGlyph(g.getPath(x, y, fontSize), chars[glyphIndex]);
        const adv = (g.advanceWidth || 0) * fontScale;
        x +=
          adv +
          kerns[glyphIndex] +
          (glyphIndex < kerns.length - 1 ? charSpacing : 0);
      });
    }

    const lastBaselineY = (lines.length - 1) * lineHeight;
    path.moveTo(0, -ascender);
    path.lineTo(0.001, -ascender);
    path.moveTo(0, lastBaselineY - descender);
    path.lineTo(0.001, lastBaselineY - descender);
  }

  return { path, glyphSegments };
}
